<template>
  <div>
    <v-autocomplete
      v-model="form.value"
      :items="fieldTypeItems"
      :value-comparator="selectComparator"
      :disabled="shouldDisableUpdate"
      :validator="vs.form.value.component"
      :label="adminText('workflowsAll.type_3deb745', 'Type')"
      box
      item-text="display"
    />
    <text-field
      v-model.trim="form.name"
      :validator="vs.form.name"
      :disabled="shouldDisableUpdate"
      :label="adminText('workflowsAll.label_74341e3', 'Label')"
      type="text"
    />
    <div v-if="['dropdown', 'checkbox', 'radio-button'].includes(form.value.type)">
      <div
        v-for="(item, index) in form.value.items.length"
        :key="index"
        style="display: flex; gap: 1rem; align-items: flex-start"
      >
        <text-field
          v-model.trim="form.value.items[index]"
          :disabled="shouldDisableUpdate"
          :label="adminText('workflowsAll.option_e31d972', 'Option')"
          type="text"
        />
        <v-icon style="margin-top: 1rem" @click="deleteOption(index)"> delete</v-icon>
      </div>
      <div
        class="add-another-container"
        :class="form.value.items[form.value.items.length - 1] === '' ? 'disabled-button' : ''"
        @click="addAnotherOption()"
      >
        <v-icon class="size-32" icon color="primary">add</v-icon>
        <div class="promo-text">{{ adminText('workflowsAll.add_another_option_c6f492f', 'Add another option') }}</div>
      </div>
    </div>
    <div v-if="form.value.type === 'consent'">
      <textarea-field v-model="form.value.item" :label="adminText('workflowsAll.text_fields_f916814', 'Text fields')" type="text" />
    </div>
    <div v-if="form.value.type === 'rich-text-editor'">
      <text-editor-field v-model="form.item" :placeholder="adminText('workflowsAll.description_55f8ebc', 'Description')" />
    </div>
    <div v-if="form.value.type === 'date'" class="date-range-container">
      <date-field
        :value="minDateValue"
        :disabled="shouldDisableUpdate"
        :max="maxDateValue || null"
        format="DD.MM.YYYY."
        :label="adminText('workflowsAll.minimum_date_9baedb2', 'Minimum date')"
        @input="setDateValidationValue('minDate', $event)"
      />
      <date-field
        :value="maxDateValue"
        :disabled="shouldDisableUpdate"
        :min="minDateValue || null"
        format="DD.MM.YYYY."
        :label="adminText('workflowsAll.maximum_date_20cc6d4', 'Maximum date')"
        @input="setDateValidationValue('maxDate', $event)"
      />
    </div>
    <div v-if="!form.value.hideRequired" class="switch-container">
      <div
        v-if="
          action === 'add' && attendeesExist && form.value.validations.hasOwnProperty('required')
        "
      >
        <p class="button-label">{{ adminText('workflowsAll.required_eed6bfb', 'Required') }}</p>
        <v-tooltip top color="#979797">
          <v-switch
            slot="activator"
            v-model="form.value.validations.required"
            color="#ff4c00"
            :disabled="true"
          />
          <span>{{ adminText('workflowsAll.cannot_set_field_as_required_if_attendees_exi_6280f33', 'Cannot set field as required if attendees exist.') }}</span>
        </v-tooltip>
      </div>
      <div v-else class="switch-container">
        <div v-if="form.value.validations.hasOwnProperty('required')">
          <p class="button-label">{{ adminText('workflowsAll.required_eed6bfb', 'Required') }}</p>
          <v-tooltip top color="#979797" :disabled="!requiredLockedByAttendees">
            <v-switch
              slot="activator"
              v-model="form.value.validations.required"
              color="#ff4c00"
              :disabled="requiredLockedByAttendees"
            />
            <span>{{ adminText('workflowsAll.cannot_make_a_field_required_once_attendees_e_06ad86c', 'Cannot make a field required once attendees exist.') }}</span>
          </v-tooltip>
        </div>
        <div>
          <p class="button-label">{{ adminText('workflowsAll.hidden_from_attendees_135bdcf', 'Hidden from attendees') }}</p>
          <v-tooltip top color="#979797" :disabled="!form.value.validations.required">
            <v-switch
              slot="activator"
              v-model="form.isPrivate"
              color="#ff4c00"
              :disabled="form.value.validations.required"
            />
            <span>{{ adminText('workflowsAll.a_required_field_cannot_be_hidden_turn_off_re_b9fefe3', 'A required field cannot be hidden. Turn off Required first.') }}</span>
          </v-tooltip>
        </div>
      </div>
      <div v-if="form.value.type === 'dropdown'">
        <p class="button-label">{{ adminText('workflowsAll.multiselect_b122c26', 'Multiselect') }}</p>
        <v-switch slot="activator" v-model="multiselect" color="#ff4c00" class="mt-0" />
      </div>
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import moment from 'moment'
import { cloneDeep } from 'lodash'

import { ATTENDEE_FIELDS_OPTIONS } from '../accreditations/attendee-fields'

export default {
  mixins: [WorkflowDisplay],
  name: 'attendee-field-form',
  props: {
    vs: {
      type: Object,
      default: () => ({}),
    },
    initialValues: {
      type: Object,
      default: () => ({}),
    },
    fields: {
      type: Array,
      default: () => [],
    },
    attendeesExist: {
      type: Boolean,
      default: false,
    },
    action: String,
  },
  data() {
    return {
      form: {
        value: { validations: { required: false } },
        isPrivate: false,
        ...this.initialValues,
      },
      newSelectField: '',
      // Deep-cloned per dialog session: selecting a type puts the option's value
      // object into the form by reference, and toggles would otherwise mutate the
      // shared module-level template (and every already-created field pointing at it).
      fieldTypes: cloneDeep(ATTENDEE_FIELDS_OPTIONS),
      multiselect: false,
      initialRequired: !!(
        this.initialValues &&
        this.initialValues.value &&
        this.initialValues.value.validations &&
        this.initialValues.value.validations.required
      ),
    }
  },

  computed: {
    fieldTypeItems() {
      return this.fieldTypes.map(option => ({ ...option, display: this.fieldTypeCaption(option.display) }))
    },
    shouldDisableUpdate() {
      return this.action === 'update' ? this.attendeesExist : false
    },
    requiredLockedByAttendees() {
      // With attendees, required can only be relaxed (on -> off), never enabled.
      // Locked by the saved value so it stays toggleable until the dialog is saved.
      return this.attendeesExist && !this.initialRequired
    },
    minDateValue() {
      return this.form.value.validations && this.form.value.validations.minDate
        ? this.form.value.validations.minDate
        : null
    },
    maxDateValue() {
      return this.form.value.validations && this.form.value.validations.maxDate
        ? this.form.value.validations.maxDate
        : null
    },
  },
  watch: {
    form: {
      handler(value) {
        if (this.form.value.validations.required) {
          this.form.isPrivate = false
        }
        this.$emit('values', value)
      },
      deep: true,
    },
    'form.value.type': {
      handler(value) {
        if (value !== 'date') {
          this.clearDateRangeValidations()
        }
      },
    },
    multiselect: {
      handler(value) {
        if (value) {
          this.form.value.component = 'select-field-multiple'
        } else {
          this.form.value.component = 'select-field'
        }
      },
    },
  },
  mounted() {
    if (this.action === 'update') {
      this.form = { value: { validations: { required: false } }, ...this.initialValues }
    } else {
      this.form.value.items = ['']
      this.form = { value: { validations: { required: false } }, ...this.initialValues }
    }
  },
  methods: {
    fieldTypeCaption(display) {
      if (!this.isAdminLocalization) return display
      switch (display) {
      case 'Description Label': return this.adminText('fieldTypes.descriptionLabel', 'Description Label')
      case 'Time': return this.adminText('fieldTypes.time', 'Time')
      case 'Date': return this.adminText('fieldTypes.date', 'Date')
      case 'Section label': return this.adminText('fieldTypes.sectionLabel', 'Section label')
      case 'Text': return this.adminText('fieldTypes.text', 'Text')
      case 'Email': return this.adminText('fieldTypes.email', 'Email')
      case 'Number': return this.adminText('fieldTypes.number', 'Number')
      case 'Dropdown': return this.adminText('fieldTypes.dropdown', 'Dropdown')
      case 'Checkbox': return this.adminText('fieldTypes.checkbox', 'Checkbox')
      case 'Consent': return this.adminText('fieldTypes.consent', 'Consent')
      case 'Radio button': return this.adminText('fieldTypes.radioButton', 'Radio button')
      case 'Text area': return this.adminText('fieldTypes.textArea', 'Text area')
      case 'Avatar photo': return this.adminText('fieldTypes.avatarPhoto', 'Avatar photo')
      case 'Country': return this.adminText('fieldTypes.country', 'Country')
      case 'Phone number': return this.adminText('fieldTypes.phoneNumber', 'Phone number')
      default: return display
      }
    },
    selectComparator(a, b) {
      return a.fieldID && b.fieldID && a.fieldID === b.fieldID
    },
    setDateValidationValue(key, value) {
      if (!this.form.value.validations) {
        this.$set(this.form.value, 'validations', { required: false })
      }

      this.$set(this.form.value.validations, key, this.formatDateValidationValue(value))
    },
    formatDateValidationValue(value) {
      if (!value) return null

      const parsed = moment(value)

      return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
    },
    clearDateRangeValidations() {
      if (!this.form.value || !this.form.value.validations) return

      this.$delete(this.form.value.validations, 'minDate')
      this.$delete(this.form.value.validations, 'maxDate')
    },
    addAnotherOption() {
      if (this.form.value.items.at(-1) === '') {
        return
      }
      this.form.value.items.push('')
    },
    deleteOption(index) {
      this.form.value.items.splice(index, 1)
    },
  },
}
</script>

<style scoped>
.disabled-button {
  opacity: 50%;
}

.promo-text {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 700;
  font-size: 1.125rem;
  letter-spacing: -0.015em;
  text-transform: uppercase;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
}

.add-another-container {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-bottom: 2rem;
  margin-top: 0.75rem;
}

::v-deep .v-input--selection-controls .v-input__slot {
  flex-direction: row-reverse;
  justify-content: flex-end;
}

.button-label {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #333333;
}

.switch-container {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  column-gap: 4.5rem;
}

.date-range-container {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

@media screen and (max-width: 600px) {
  .date-range-container {
    grid-template-columns: 1fr;
    gap: 0;
  }
}
</style>
